import React from "react";
import { X, Circle, GitBranch, AlertTriangle } from "lucide-react";

const LEGEND = [
  { color: "#D92D20", label: "Anchor", body: "The subject of the file — every relationship is read in relation to this node." },
  { color: "#9FA8D4", label: "Person", body: "An individual with at least one documented public connection." },
  { color: "#E0A526", label: "Organisation / Company", body: "A business, foundation, or institution referenced in the record." },
  { color: "#5DD4B3", label: "Document / Filing", body: "A court filing, deposition, log, or primary record cited inline." },
];

const TIERS = [
  { color: "#5DD4B3", label: "Documented", body: "Backed by a primary document or court record." },
  { color: "#9FA8D4", label: "Reported", body: "Reported by an established outlet with editorial oversight." },
  { color: "#E0A526", label: "Alleged", body: "Sworn testimony or credible witness claim. Clearly labeled." },
];

export const HowToReadModal = ({ onClose }) => (
  <>
    <button
      type="button"
      aria-label="Close guide"
      onClick={onClose}
      className="fixed inset-0 z-40 bg-black/75 backdrop-blur-sm"
      data-testid="ef-howto-overlay"
    />
    <div
      className="fixed z-50 inset-x-2 top-4 bottom-4 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:top-10 sm:bottom-10 sm:w-[680px] overflow-y-auto bg-[#0F0F0F] border border-[#2D2A26]"
      data-testid="ef-howto-modal"
    >
      <div className="px-5 py-4 border-b border-[#2D2A26] flex items-center justify-between sticky top-0 bg-[#0F0F0F] z-10">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// READING GUIDE</div>
          <div className="font-display text-2xl text-[#E4DFD3] mt-1">How to read this map</div>
        </div>
        <button
          onClick={onClose}
          data-testid="ef-howto-close"
          className="text-[#A39E93] hover:text-[#E4DFD3] p-1"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="p-5 space-y-7">
        {/* The premise */}
        <section>
          <p className="text-sm text-[#E4DFD3] leading-relaxed">
            This is an <span className="text-[#D92D20]">evidence map</span>, not a
            rumor board. Every node is a person, organisation, or document with at
            least one public, source-backed connection to the anchor.
          </p>
        </section>

        {/* What it does NOT mean */}
        <section className="border border-[#D92D20]/40 bg-[#1a0808] p-4">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">
            <AlertTriangle size={12} /> WHAT INCLUSION DOES NOT MEAN
          </div>
          <ul className="text-sm text-[#CBB892] space-y-1.5 leading-relaxed">
            <li>— Appearance here is not an accusation.</li>
            <li>— A documented connection is not a crime.</li>
            <li>— Proximity to a name on this map is not guilt.</li>
            <li>— We do not practice guilt by association. Read the source.</li>
          </ul>
        </section>

        {/* Node legend */}
        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] mb-3">// NODES</div>
          <div className="space-y-2">
            {LEGEND.map((l) => (
              <div key={l.label} className="flex items-start gap-3 border border-[#2D2A26] bg-[#0a0907] px-3 py-2">
                <Circle size={14} fill={l.color} stroke="none" className="mt-1 shrink-0" />
                <div className="min-w-0">
                  <div className="font-heading text-sm text-[#E4DFD3]">{l.label}</div>
                  <div className="text-xs text-[#A39E93] leading-relaxed">{l.body}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Edges */}
        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] mb-3">// CONNECTIONS</div>
          <div className="flex items-start gap-3 border border-[#2D2A26] bg-[#0a0907] px-3 py-2">
            <GitBranch size={14} className="text-[#D92D20] mt-1 shrink-0" />
            <p className="text-xs text-[#A39E93] leading-relaxed">
              Each line is a documented relationship — a flight, a transaction, a
              filing, a meeting, a record. Click a line to see the relationship
              type, date range, legal note, and the sources behind it.
            </p>
          </div>
        </section>

        {/* Confidence tiers */}
        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] mb-3">// CONFIDENCE</div>
          <div className="border border-[#2D2A26] bg-[#0a0907] divide-y divide-[#2D2A26]">
            {TIERS.map((t) => (
              <div key={t.label} className="flex items-start gap-3 px-3 py-2">
                <span
                  className="font-mono text-[10px] uppercase tracking-[0.3em] shrink-0 w-24"
                  style={{ color: t.color }}
                >
                  {t.label}
                </span>
                <p className="text-xs text-[#A39E93] leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Found a mistake */}
        <section className="border border-[#2D2A26] bg-[#0F0F0F] p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">// FOUND A MISTAKE?</div>
          <p className="text-xs text-[#A39E93] leading-relaxed">
            Every claim links to its source. If a record contradicts the document
            behind it, use <span className="text-[#E4DFD3]">Submit to the file</span> to
            send a correction or dispute. Editors review every flag.
          </p>
        </section>
      </div>

      <div className="px-5 py-4 border-t border-[#2D2A26] flex justify-end sticky bottom-0 bg-[#0F0F0F]">
        <button
          onClick={onClose}
          data-testid="ef-howto-got-it"
          className="bg-[#D92D20] hover:bg-[#B92318] text-white px-5 py-2 font-mono text-[10px] uppercase tracking-[0.3em]"
        >
          Got it — open the file
        </button>
      </div>
    </div>
  </>
);

export default HowToReadModal;
